import { ElementRef, Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import { FeUtil } from '../../../hezky-routes';
import { MetaData } from '../components/forms/forms.component';

/**
 * Metadata required to generate the incident form report filename.
 */
export interface IncidentFormPdfMetaData {
  id?: number | string;
  leg_id?: number | string;
  incident_id?: string;
}

/**
 * Structure of form report data used for PDF generation.
 * Mirrors the payload from getFormData / incident form report component.
 */
export interface IncidentFormReportData {
  prod?: {
    excursionDetails?: Record<string, unknown>;
    affectedProductList?: Array<{
      product_name?: string | number;
      batch_no?: string | number;
      units_affected?: string | number;
      isSelected?: number;
    }>;
  };
  capa?: {
    capaDetails?: Record<string, unknown>;
    documents?: Array<{ doc_name?: string; doc_link?: string }>;
  };
  eval?: {
    evalDispDetails?: Record<string, unknown>;
  };
}

/**
 * Service that generates the incident form report PDF programmatically (no HTML capture).
 * Layout mirrors the incident-form-report component: Incident Details, CAPA Form, Evaluation Disposition.
 */
@Injectable({
  providedIn: 'root',
})
export class IncidentFormPdfService {
  private static readonly PAGE_WIDTH = 595.28;
  private static readonly PAGE_HEIGHT = 841.89;
  private static readonly MARGIN = 20;
  private static readonly BORDER_WIDTH = 0.5;
  private static readonly LINE_HEIGHT = 14;
  private static readonly SECTION_GAP = 18;
  private static readonly PADDING = 18;
  private static readonly SECTION_HEADER_HEIGHT = 22;
  private static readonly LABEL_GRAY: [number, number, number] = [100, 116, 139];
  /** Primary brand blue from the HEZKY logo (#1370B0). */
  private static readonly BRAND_BLUE: [number, number, number] = [19, 112, 176];
  /** Light tint of brand blue for backgrounds. */
  private static readonly BRAND_BLUE_LIGHT: [number, number, number] = [232, 245, 255];
  private static readonly RED_HEADING: [number, number, number] = [180, 35, 24];
  private static readonly SECTION_BORDER: [number, number, number] = [203, 213, 225];
  private static readonly SECTION_TITLE_COLOR: [number, number, number] = [30, 41, 59];
  private static readonly PAGE_BORDER: [number, number, number] = [226, 232, 240];
  /** Height of the branding header drawn at the top of every page. */
  private static readonly PAGE_HEADER_HEIGHT = 56;
  /** Height of the footer drawn at the bottom of every page. */
  private static readonly PAGE_FOOTER_HEIGHT = 22;

  /** Font size for key in key: value pairs. */
  private static readonly KEY_VALUE_FONT_SIZE = 9;
  /** Spacing between key-value pairs. */
  private static readonly KEY_VALUE_GAP = 6;

  constructor(private readonly feUtil: FeUtil) {}

  /**
   * Writes a left-aligned key: value pair with value wrapping to avoid overflow.
   * Reserves space and adds page when needed.
   */
  private static writeKeyValue(
    pdf: jsPDF,
    key: string,
    value: string | Array<{ text: string; url?: string | null }>,
    leftX: number,
    contentWidth: number,
    lineHeight: number,
    y: { current: number },
    ensureSpace: (needed: number, onNewPage?: () => void) => void
  ): void {
    const keyPrefix = `${key}: `;
    const isLinkArray = Array.isArray(value);
    const valueText = isLinkArray ? '' : (value ?? '-');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(IncidentFormPdfService.KEY_VALUE_FONT_SIZE);
    pdf.setTextColor(...IncidentFormPdfService.LABEL_GRAY);
    const keyWidth = pdf.getTextWidth(keyPrefix);
    const valueMaxWidth = Math.max(20, contentWidth - keyWidth - 8);
    const valueLines = isLinkArray
      ? (value as Array<{ text: string }>).map(v => v.text ?? '-')
      : pdf.splitTextToSize(valueText, valueMaxWidth);
    const lineAdvance = lineHeight * 0.9;
    const blockHeight = lineHeight * 0.35 + valueLines.length * lineAdvance + IncidentFormPdfService.KEY_VALUE_GAP;
    ensureSpace(blockHeight);
    const startY = y.current;
    const baselineY = startY + lineHeight * 0.35;
    pdf.text(keyPrefix, leftX, baselineY);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);

    // First line stays on the same baseline as the key
    if (isLinkArray) {
      const firstItem = (value as Array<{ text: string; url?: string | null }>)[0];
      const firstText = firstItem?.text ?? '-';
      const firstUrl = firstItem?.url ?? undefined;
      if (firstUrl && typeof (pdf as any).textWithLink === 'function') {
        (pdf as any).textWithLink(firstText, leftX + keyWidth, baselineY, { url: firstUrl });
      } else {
        pdf.text(firstText, leftX + keyWidth, baselineY);
      }
    } else {
      pdf.text(valueLines[0], leftX + keyWidth, baselineY);
    }

    // Remaining lines rendered below, starting from leftX
    for (let i = 1; i < valueLines.length; i++) {
      const lineY = baselineY + i * lineAdvance;
      if (isLinkArray) {
        const item = (value as Array<{ text: string; url?: string | null }>)[i];
        const lineText = item?.text ?? valueLines[i];
        const lineUrl = item?.url ?? undefined;
        if (lineUrl && typeof (pdf as any).textWithLink === 'function') {
          (pdf as any).textWithLink(lineText, leftX, lineY, { url: lineUrl });
        } else {
          pdf.text(lineText, leftX, lineY);
        }
      } else {
        pdf.text(valueLines[i], leftX, lineY);
      }
    }
    y.current = startY + lineHeight * 0.35 + valueLines.length * lineAdvance + IncidentFormPdfService.KEY_VALUE_GAP;
  }

  /**
   * Draws the branded page header: blue top bar, report title, report ref, and HEZKY logo.
   * Called on every page (first page and each new page added).
   */
  private static drawPageHeader(
    pdf: jsPDF,
    margin: number,
    logoImgData: string | null,
    reportLabel: string
  ): void {
    const pageWidth = IncidentFormPdfService.PAGE_WIDTH;
    const padding = IncidentFormPdfService.PADDING;
    const contentLeft = margin + padding;
    const contentRight = pageWidth - contentLeft;

    // Blue accent bar at top of page frame
    pdf.setFillColor(...IncidentFormPdfService.BRAND_BLUE);
    pdf.rect(margin, margin, pageWidth - margin * 2, 4, 'F');

    const titleBaseY = margin + 4 + 18;

    // Report title
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...IncidentFormPdfService.BRAND_BLUE);
    pdf.text('INCIDENT FORM REPORT', contentLeft, titleBaseY);

    // Incident reference line
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...IncidentFormPdfService.LABEL_GRAY);
    pdf.text(reportLabel, contentLeft, titleBaseY + 13);

    // Logo on the right side of the header
    // V2 logo native size: 1048 × 1416 (portrait, aspect ≈ 0.74)
    if (logoImgData) {
      const logoH = 36;
      const logoW = Math.round(logoH * (1048 / 1416));
      pdf.addImage(logoImgData, 'PNG', contentRight - logoW, margin + 6, logoW, logoH);
    } else {
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...IncidentFormPdfService.BRAND_BLUE);
      pdf.text('HEZKY', contentRight, titleBaseY, { align: 'right' });
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text('TECH', contentRight, titleBaseY + 10, { align: 'right' });
    }

    // Separator line beneath header
    pdf.setDrawColor(...IncidentFormPdfService.SECTION_BORDER);
    pdf.setLineWidth(0.5);
    const sepY = margin + IncidentFormPdfService.PAGE_HEADER_HEIGHT - 4;
    pdf.line(contentLeft, sepY, contentRight, sepY);

    pdf.setTextColor(0, 0, 0);
  }

  /**
   * Draws the page footer: thin separator line, report ID on left, page number on right.
   * Called after all content is written by iterating all pages.
   */
  private static drawPageFooter(
    pdf: jsPDF,
    pageNum: number,
    totalPages: number,
    margin: number,
    reportLabel: string
  ): void {
    const pageWidth = IncidentFormPdfService.PAGE_WIDTH;
    const pageHeight = IncidentFormPdfService.PAGE_HEIGHT;
    const padding = IncidentFormPdfService.PADDING;
    const contentLeft = margin + padding;
    const contentRight = pageWidth - contentLeft;
    const lineY = pageHeight - margin - IncidentFormPdfService.PAGE_FOOTER_HEIGHT + 4;
    const textY = lineY + 10;

    pdf.setDrawColor(...IncidentFormPdfService.SECTION_BORDER);
    pdf.setLineWidth(0.4);
    pdf.line(contentLeft, lineY, contentRight, lineY);

    pdf.setFontSize(7.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...IncidentFormPdfService.LABEL_GRAY);
    pdf.text(reportLabel, contentLeft, textY);
    pdf.text(`Page ${pageNum} of ${totalPages}`, contentRight, textY, { align: 'right' });
    pdf.setTextColor(0, 0, 0);
  }

  /**
   * Draws a section header with brand blue left accent bar and light blue background.
   */
  private static drawSectionHeader(
    pdf: jsPDF,
    title: string,
    leftX: number,
    boxWidth: number,
    y: number,
    lineHeight: number
  ): number {
    const headerHeight = IncidentFormPdfService.SECTION_HEADER_HEIGHT;
    const accentWidth = 3.5;

    // Light blue background
    pdf.setFillColor(...IncidentFormPdfService.BRAND_BLUE_LIGHT);
    pdf.rect(leftX, y, boxWidth, headerHeight, 'F');

    // Brand blue left accent bar
    pdf.setFillColor(...IncidentFormPdfService.BRAND_BLUE);
    pdf.rect(leftX, y, accentWidth, headerHeight, 'F');

    // Section title
    pdf.setFontSize(10.5);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...IncidentFormPdfService.SECTION_TITLE_COLOR);
    const textY = y + lineHeight * 0.85;
    pdf.text(title, leftX + accentWidth + 8, textY);

    // Bottom border
    pdf.setDrawColor(...IncidentFormPdfService.SECTION_BORDER);
    pdf.setLineWidth(0.5);
    pdf.line(leftX, y + headerHeight, leftX + boxWidth, y + headerHeight);

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);

    return y + headerHeight;
  }

  /**
   * Loads the HEZKY V2 logo and returns a compact PNG data URL suitable for PDF embedding.
   * The V2 logo is an SVG wrapping a base64-encoded PNG — the embedded PNG is extracted
   * directly so the border design is preserved without re-rendering the full SVG.
   * Resized to a small canvas (3× retina at 36pt) to keep PDF file size minimal.
   * Returns null on any failure so PDF generation continues with the text fallback.
   */
  private async loadLogoDataUrl(): Promise<string | null> {
    try {
      const response = await fetch('/assets/svg/hezky_logo_V2.svg');
      if (!response.ok) return null;
      const svgText = await response.text();

      // Extract the embedded base64 PNG from the SVG wrapper
      const match = svgText.match(/xlink:href="(data:image\/[^;]+;base64,[^"]+)"/);
      const imgSrc = match ? match[1] : null;

      if (!imgSrc) return null;

      return new Promise<string | null>((resolve) => {
        const img = new Image();
        img.onload = () => {
          // Render at 3× density (target display: 36pt tall → 108px)
          const targetH = 108;
          const targetW = Math.round(targetH * (img.naturalWidth / img.naturalHeight));
          const canvas = document.createElement('canvas');
          canvas.width = targetW;
          canvas.height = targetH;
          const ctx = canvas.getContext('2d');
          if (!ctx) { resolve(null); return; }
          ctx.clearRect(0, 0, targetW, targetH);
          ctx.drawImage(img, 0, 0, targetW, targetH);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => resolve(null);
        img.src = imgSrc;
      });
    } catch {
      return null;
    }
  }

  /**
   * Generates the incident form report PDF from the given form data and triggers a browser download.
   */
  async generateAndDownload(
    formData: IncidentFormReportData | null,
    metaData: MetaData,
    graphTemplate: ElementRef<HTMLDivElement>
  ): Promise<boolean> {
    if (!formData) {
      this.feUtil.message.warning('No report data available to download.');
      return false;
    }

    const pdf = await this.buildPdf(formData, graphTemplate, metaData);
    const fileName = this.getFileName(metaData);
    pdf.save(fileName);
    return true;
  }

  /**
   * Builds the jsPDF document from form data. Exposed for tests or alternate output (e.g. blob).
   */
  async buildPdf(formData: IncidentFormReportData, graphTemplate: ElementRef<HTMLDivElement>, metaData: MetaData): Promise<jsPDF> {
    const margin = IncidentFormPdfService.MARGIN;
    const padding = IncidentFormPdfService.PADDING;
    const pageHeight = IncidentFormPdfService.PAGE_HEIGHT;
    const boxX = margin + padding;
    const boxWidth = IncidentFormPdfService.PAGE_WIDTH - boxX * 2;
    const contentWidth = boxWidth;
    const lineHeight = IncidentFormPdfService.LINE_HEIGHT;
    const sectionGap = IncidentFormPdfService.SECTION_GAP;
    const sectionInnerPadding = 12;
    const headerHeight = IncidentFormPdfService.PAGE_HEADER_HEIGHT;
    const footerHeight = IncidentFormPdfService.PAGE_FOOTER_HEIGHT;

    // Load logo before layout begins so all pages share the same data URL
    const logoImgData = await this.loadLogoDataUrl();

    const reportId = this.feUtil.appId === 2
      ? `T${metaData?.id ?? '-'}L${metaData?.leg_id ?? '-'}`
      : String(metaData?.incident_id ?? metaData?.id ?? '-');
    const reportLabel = `Incident: ${reportId}`;

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

    const drawPageFrame = (): void => {
      pdf.setDrawColor(...IncidentFormPdfService.PAGE_BORDER);
      pdf.setLineWidth(IncidentFormPdfService.BORDER_WIDTH);
      pdf.rect(margin, margin, IncidentFormPdfService.PAGE_WIDTH - margin * 2, pageHeight - margin * 2);
    };

    const drawHeader = (): void => {
      IncidentFormPdfService.drawPageHeader(pdf, margin, logoImgData, reportLabel);
    };

    drawPageFrame();
    drawHeader();

    // Content area starts below header and ends above footer
    let y = margin + headerHeight + 6;
    const pageBottomY = pageHeight - margin - footerHeight - 6;

    const ensureSpace = (needed: number, onNewPage?: () => void): void => {
      if (y + needed > pageBottomY) {
        pdf.addPage();
        drawPageFrame();
        drawHeader();
        y = margin + headerHeight + 6;
        if (onNewPage) onNewPage();
      }
    };

    const addPageForTable = (): number => {
      pdf.addPage();
      drawPageFrame();
      drawHeader();
      y = margin + headerHeight + 6;
      return y;
    };

    const leftX = boxX;

    const writeLine = (text: string, fontSize: number, isBold = false): void => {
      ensureSpace(lineHeight + 4);
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
      const lines = pdf.splitTextToSize(text, contentWidth - 4);
      for (const line of lines) {
        ensureSpace(lineHeight);
        pdf.text(line, leftX + 4, y + lineHeight * 0.35);
        y += lineHeight;
      }
    };

    const formatDate = (value: Date | string | number | null | undefined, format = 'MMM dd yyyy, HH:mm a'): string =>
      this.formatReportDate(value, format);

    const prod = formData?.prod?.excursionDetails as Record<string, unknown> | undefined;
    const affectedProductList = formData?.prod?.affectedProductList ?? [];
    const capa = formData?.capa?.capaDetails as Record<string, unknown> | undefined;
    const capaFiles = formData?.capa?.documents ?? [];
    const evalDisp = formData?.eval?.evalDispDetails as Record<string, unknown> | undefined;
    const hasProd = prod && Object.keys(prod).length > 0;
    const hasCapa = capa && Object.keys(capa).length > 0;
    const hasEval = evalDisp && Object.keys(evalDisp).length > 0;

    if (!hasProd && !hasCapa && !hasEval) {
      writeLine('No data found', 11);
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        IncidentFormPdfService.drawPageFooter(pdf, i, totalPages, margin, reportLabel);
      }
      return pdf;
    }

    // Metadata info card
    const metaBlockH = lineHeight * 3 + 14;
    ensureSpace(metaBlockH + sectionGap);

    pdf.setFillColor(...IncidentFormPdfService.BRAND_BLUE_LIGHT);
    pdf.roundedRect(leftX, y, contentWidth, metaBlockH, 3, 3, 'F');
    pdf.setDrawColor(...IncidentFormPdfService.SECTION_BORDER);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(leftX, y, contentWidth, metaBlockH, 3, 3, 'S');

    // Blue left accent on metadata card
    pdf.setFillColor(...IncidentFormPdfService.BRAND_BLUE);
    pdf.roundedRect(leftX, y, 3.5, metaBlockH, 1.5, 1.5, 'F');

    const metaInnerX = leftX + 12;
    const metaBaseY = y + 11;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...IncidentFormPdfService.SECTION_TITLE_COLOR);

    pdf.setFontSize(8.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...IncidentFormPdfService.LABEL_GRAY);
    pdf.text(`Generated by: ${this.feUtil.user_name}`, metaInnerX, metaBaseY + lineHeight);
    pdf.text(`Generated at: ${this.formatReportDate(new Date())}`, metaInnerX, metaBaseY + lineHeight * 2);

    pdf.setTextColor(0, 0, 0);
    y += metaBlockH + sectionGap;

    if (hasProd) {
      ensureSpace(IncidentFormPdfService.SECTION_HEADER_HEIGHT + sectionInnerPadding + lineHeight * 2);
      y = IncidentFormPdfService.drawSectionHeader(pdf, 'Incident Details', boxX, boxWidth, y, lineHeight);
      y += sectionInnerPadding;

      const yRef = { current: y };
      const contentWidthInner = contentWidth - 8;

      IncidentFormPdfService.writeKeyValue(
        pdf,
        'Product Impact (Impacted?)',
        prod?.['isImpacted'] === 'Yes' ? 'Yes' : prod?.['isImpacted'] === 'No' ? 'No' : '-',
        leftX + 4,
        contentWidthInner,
        lineHeight,
        yRef,
        ensureSpace
      );
      IncidentFormPdfService.writeKeyValue(
        pdf,
        'Relocation (Shifted?)',
        prod?.['shiftedYesNo'] === 'Yes' ? 'Yes' : 'No',
        leftX + 4,
        contentWidthInner,
        lineHeight,
        yRef,
        ensureSpace
      );
      if (prod?.['shiftedYesNo'] === 'Yes' && this.feUtil.appId === 2) {
        IncidentFormPdfService.writeKeyValue(
          pdf,
          'Shifted To',
          String(prod?.['shiftedTo'] ?? 'No'),
          leftX + 4,
          contentWidthInner,
          lineHeight,
          yRef,
          ensureSpace
        );
        IncidentFormPdfService.writeKeyValue(
          pdf,
          'Date & Time Shifted',
          formatDate(prod?.['shiftDatetime'] as string | number | null),
          leftX + 4,
          contentWidthInner,
          lineHeight,
          yRef,
          ensureSpace
        );
        IncidentFormPdfService.writeKeyValue(
          pdf,
          'Shifted to detail',
          String(prod?.['shiftTargetName'] as string | number | null),
          leftX + 4,
          contentWidthInner,
          lineHeight,
          yRef,
          ensureSpace
        );
      }
      IncidentFormPdfService.writeKeyValue(
        pdf,
        'Details',
        String(prod?.['immedCorrDetail'] ?? '-'),
        leftX + 4,
        contentWidthInner,
        lineHeight,
        yRef,
        ensureSpace
      );
      y = yRef.current;
      if (prod?.['isImpacted'] === 'Yes' && affectedProductList.length > 0) {
        y = this.writeAffectedProductsTable(pdf, affectedProductList, contentWidth, leftX, lineHeight, y, ensureSpace, sectionGap, false, addPageForTable);
      }
      y += sectionGap;
    }
    if (hasCapa) {
      ensureSpace(IncidentFormPdfService.SECTION_HEADER_HEIGHT + sectionInnerPadding + lineHeight * 2);
      y = IncidentFormPdfService.drawSectionHeader(pdf, 'CAPA Form', boxX, boxWidth, y, lineHeight);
      y += sectionInnerPadding;

      const capaYRef = { current: y };
      const contentWidthInner = contentWidth - 8;

      if (this.feUtil.appId === 2) {
        IncidentFormPdfService.writeKeyValue(
          pdf,
          'Was the vehicle sealed?',
          capa?.['isVehicleSealed'] === 'Yes' ? 'Yes' : capa?.['isVehicleSealed'] === 'No' ? 'No' : '-',
          leftX + 4,
          contentWidthInner,
          lineHeight,
          capaYRef,
          ensureSpace
        );
        IncidentFormPdfService.writeKeyValue(
          pdf,
          'Was the cooling working when vehicle was received and being updated at CFA/HUB?',
          capa?.['isCoolingWorking'] === 'Yes' ? 'Yes' : capa?.['isCoolingWorking'] === 'No' ? 'No' : '-',
          leftX + 4,
          contentWidthInner,
          lineHeight,
          capaYRef,
          ensureSpace
        );
        IncidentFormPdfService.writeKeyValue(
          pdf,
          'Discuss with driver to find out if vehicle or cooling unit malfunctioned during transit?',
          capa?.['isMalfunctionReported'] === 'Yes' ? 'Yes' : capa?.['isMalfunctionReported'] === 'No' ? 'No' : '-',
          leftX + 4,
          contentWidthInner,
          lineHeight,
          capaYRef,
          ensureSpace
        );
        IncidentFormPdfService.writeKeyValue(
          pdf,
          'Details',
          capa?.['details'] as string,
          leftX + 4,
          contentWidthInner,
          lineHeight,
          capaYRef,
          ensureSpace
        );
      }

      IncidentFormPdfService.writeKeyValue(
        pdf,
        'Reason for Excursion',
        capa?.['reasonForExcursion'] as string,
        leftX + 4,
        contentWidthInner,
        lineHeight,
        capaYRef,
        ensureSpace
      );

      if (this.feUtil.appId === 1) {
        IncidentFormPdfService.writeKeyValue(
          pdf,
          'Excursion Details',
          capa?.['details'] as string,
          leftX + 4,
          contentWidthInner,
          lineHeight,
          capaYRef,
          ensureSpace
        );
        IncidentFormPdfService.writeKeyValue(
          pdf,
          'Details',
          capa?.['comment'] as string,
          leftX + 4,
          contentWidthInner,
          lineHeight,
          capaYRef,
          ensureSpace
        );
      }

      IncidentFormPdfService.writeKeyValue(
        pdf,
        'CAPA Implementation Date',
        formatDate(capa?.['capaImpDate'] as string | number | null, 'MMM d, yyyy') || '-',
        leftX + 4,
        contentWidthInner,
        lineHeight,
        capaYRef,
        ensureSpace
      );

      const docsLabel = 'Documents Attached';
      const docsValue = capaFiles.length > 0
        ? capaFiles.map((d, i) => ({
            text: `${i + 1}. ${d?.doc_name ?? '-'}`,
            url: d?.doc_link ?? undefined
          }))
        : [{ text: 'No documents attached.' }];
      IncidentFormPdfService.writeKeyValue(
        pdf,
        docsLabel,
        docsValue,
        leftX + 4,
        contentWidthInner,
        lineHeight,
        capaYRef,
        ensureSpace
      );

      y = capaYRef.current;
      y += sectionGap;
    }
    if (hasEval) {
      ensureSpace(IncidentFormPdfService.SECTION_HEADER_HEIGHT + sectionInnerPadding + lineHeight * 2);
      y = IncidentFormPdfService.drawSectionHeader(pdf, 'Evaluation Disposition', boxX, boxWidth, y, lineHeight);
      y += sectionInnerPadding;

      const evalYRef = { current: y };
      const contentWidthInner = contentWidth - 8;
      IncidentFormPdfService.writeKeyValue(
        pdf,
        'Product Disposition',
        String(evalDisp?.['productDisposition'] ?? '-'),
        leftX + 4,
        contentWidthInner,
        lineHeight,
        evalYRef,
        ensureSpace
      );
      IncidentFormPdfService.writeKeyValue(
        pdf,
        'Details',
        String(evalDisp?.['impactAssessment'] ?? '-'),
        leftX + 4,
        contentWidthInner,
        lineHeight,
        evalYRef,
        ensureSpace
      );
      y = evalYRef.current;

      if (prod?.['isImpacted'] === 'Yes' && affectedProductList.length > 0) {
        y = this.writeAffectedProductsTable(pdf, affectedProductList, contentWidth, leftX, lineHeight, y, ensureSpace, sectionGap, true, addPageForTable);
      }
      y += sectionGap;
    }
    if (graphTemplate) {
      y = await this.writeGraph(
        pdf,
        graphTemplate.nativeElement,
        contentWidth,
        leftX,
        lineHeight,
        y,
        ensureSpace,
        sectionGap
      );
    }

    // Add footers to all pages now that total page count is known
    const totalPages = pdf.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      IncidentFormPdfService.drawPageFooter(pdf, i, totalPages, margin, reportLabel);
    }

    return pdf;
  }

  /**
   * Renders the TR/Storage temperature graph at the end of the report.
   */
  private async writeGraph(
    pdf: jsPDF,
    graphElement: HTMLDivElement,
    contentWidth: number,
    leftX: number,
    lineHeight: number,
    y: number,
    ensureSpace: (needed: number, onNewPage?: () => void) => void,
    sectionGap: number
  ): Promise<number> {
    if (!graphElement) return Promise.resolve(y);

    try {
      const canvas = await html2canvas(graphElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const canvasWidth = canvas.width || 1;
      const canvasHeight = canvas.height || 1;

      const targetWidth = Math.min(contentWidth, canvasWidth);
      const scale = targetWidth / canvasWidth;
      const targetHeight = canvasHeight * scale;

      const paddingTop = sectionGap;
      const paddingBottom = sectionGap;

      ensureSpace(paddingTop + targetHeight + paddingBottom + lineHeight, () => {
        y = IncidentFormPdfService.MARGIN + IncidentFormPdfService.PAGE_HEADER_HEIGHT + 6;
      });

      const x = leftX;
      const imageY = y + paddingTop;

      pdf.addImage(imgData, 'PNG', x, imageY, targetWidth, targetHeight);

      pdf.setDrawColor(...IncidentFormPdfService.SECTION_BORDER);
      pdf.setLineWidth(2);
      pdf.rect(x, imageY, targetWidth, targetHeight, 'S');

      y = imageY + targetHeight + paddingBottom;
      return y;
    } catch {
      return y;
    }
  }

  /**
   * Writes the Affected Products table (Incident Details or Evaluation section).
   * Handles page breaks by redrawing the column header on each new page and drawing
   * vertical dividers only within each page segment.
   */
  private writeAffectedProductsTable(
    pdf: jsPDF,
    affectedProductList: NonNullable<IncidentFormReportData['prod']>['affectedProductList'],
    contentWidth: number,
    marginOrLeftX: number,
    lineHeight: number,
    y: number,
    ensureSpace: (needed: number, onNewPage?: () => void) => void,
    sectionGap: number,
    includeActionTaken: boolean,
    addPageForTable: () => number
  ): number {
    const leftX = marginOrLeftX;
    ensureSpace(lineHeight * 2, () => {
      y = IncidentFormPdfService.MARGIN + IncidentFormPdfService.PAGE_HEADER_HEIGHT + 6;
    });
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...IncidentFormPdfService.RED_HEADING);
    pdf.text('Affected Products', leftX + 4, y + lineHeight * 0.35);
    y += lineHeight + 4;
    pdf.setTextColor(0, 0, 0);

    const safeWidth = contentWidth - 2 * 2;
    let colW: number[];
    if (includeActionTaken) {
      colW = [safeWidth * 0.3, safeWidth * 0.2, safeWidth * 0.22, safeWidth * 0.28];
    } else {
      colW = [safeWidth * 0.4, safeWidth * 0.25, safeWidth * 0.35];
    }

    const x0 = leftX + 4 + 2;
    const tableWidth = colW.reduce((acc, v) => acc + v, 0);

    /**
     * Draws the blue header row at position `headerY` and returns the Y after it.
     */
    const drawTableHeader = (headerY: number): number => {
      pdf.setFillColor(...IncidentFormPdfService.BRAND_BLUE);
      pdf.rect(x0 - 2, headerY, tableWidth + 4, lineHeight + 2, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(255, 255, 255);
      pdf.text('Product Name', x0, headerY + lineHeight * 0.7);
      pdf.text('Batch No.', x0 + colW[0], headerY + lineHeight * 0.7);
      pdf.text('No. Of Units Affected', x0 + colW[0] + colW[1], headerY + lineHeight * 0.7);
      if (includeActionTaken) {
        pdf.text('Action Taken', x0 + colW[0] + colW[1] + colW[2], headerY + lineHeight * 0.7);
      }
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);
      return headerY + lineHeight + 2;
    };

    /**
     * Draws vertical column dividers for a completed page segment from `segStartY` to `segEndY`.
     */
    const drawSegmentDividers = (segStartY: number, segEndY: number): void => {
      pdf.setDrawColor(...IncidentFormPdfService.SECTION_BORDER);
      pdf.setLineWidth(0.5);
      for (let i = 1; i < colW.length; ++i) {
        const divX = x0 - 2 + colW.slice(0, i).reduce((a, b) => a + b, 0);
        pdf.line(divX, segStartY, divX, segEndY);
      }
      pdf.line(x0 - 2 + tableWidth + 4, segStartY, x0 - 2 + tableWidth + 4, segEndY);
    };

    // Draw initial header and record the start of the first page segment
    y = drawTableHeader(y);
    let segmentStartY = y - (lineHeight + 2); // segment starts at top of header

    const pageBottomY = IncidentFormPdfService.PAGE_HEIGHT - IncidentFormPdfService.MARGIN - IncidentFormPdfService.PAGE_FOOTER_HEIGHT - 6;

    const list = affectedProductList ?? [];
    let rowIndex = 0;
    for (const p of list) {
      if (y + lineHeight + 4 > pageBottomY) {
        // Close current segment dividers before page break
        drawSegmentDividers(segmentStartY, y);
        // Add new page (draws frame + header, returns new y)
        y = addPageForTable();
        // Redraw table column header on new page
        y = drawTableHeader(y);
        segmentStartY = y - (lineHeight + 2);
        rowIndex = 0;
      }

      // Alternate row background for readability
      if (rowIndex % 2 === 0) {
        pdf.setFillColor(...IncidentFormPdfService.BRAND_BLUE_LIGHT);
        pdf.rect(x0 - 2, y, tableWidth + 4, lineHeight, 'F');
      }

      pdf.setDrawColor(...IncidentFormPdfService.SECTION_BORDER);
      pdf.setLineWidth(0.5);
      pdf.rect(x0 - 2, y, tableWidth + 4, lineHeight, 'S');

      const name = (p?.product_name ?? '-').toString().slice(0, includeActionTaken ? 28 : 35);
      pdf.text(name, x0, y + lineHeight * 0.7);
      pdf.text((p?.batch_no ?? '-').toString(), x0 + colW[0], y + lineHeight * 0.7);
      pdf.text((p?.units_affected ?? '-').toString(), x0 + colW[0] + colW[1], y + lineHeight * 0.7);
      if (includeActionTaken) {
        const action = p?.isSelected === 1 ? 'Released' : p?.isSelected === -1 ? 'Rejected' : '-';
        pdf.text(action, x0 + colW[0] + colW[1] + colW[2], y + lineHeight * 0.7);
      }
      y += lineHeight;
      rowIndex++;
    }

    // Draw dividers for the final (or only) page segment
    drawSegmentDividers(segmentStartY, y);

    y += sectionGap;
    return y;
  }

  /**
   * Returns the report filename based on selected application and metadata.
   */
  getFileName(metaData: MetaData): string {
    if (this.feUtil.appId === 2) {
      return `T${metaData?.id}L${metaData?.leg_id}-form-report.pdf`;
    }
    if (this.feUtil.appId === 1) {
      return `${metaData?.incident_id}-report.pdf`;
    }
    return 'report.pdf';
  }

  /**
   * Formats a date value for display in the report PDF using the app time zone.
   */
  formatReportDate(value: Date | string | number | null | undefined, format = 'MMM dd yyyy, HH:mm a'): string {
    if (value == null) return '-';
    let dt: DateTime | null = null;
    if (typeof value === 'number') {
      dt = DateTime.fromMillis(value, { zone: this.feUtil.time_zone });
    } else if (typeof value === 'string') {
      dt = DateTime.fromISO(value, { zone: this.feUtil.time_zone });
      if (!dt.isValid) dt = DateTime.fromSQL(value, { zone: this.feUtil.time_zone });
      if (!dt.isValid) {
        const d = new Date(value);
        if (!isNaN(d.getTime())) dt = DateTime.fromJSDate(d, { zone: this.feUtil.time_zone });
      }
    } else if (value instanceof Date) {
      dt = DateTime.fromJSDate(value, { zone: this.feUtil.time_zone });
    }
    if (!dt?.isValid) return '-';
    try {
      return dt.toFormat(format);
    } catch {
      return dt.toISO() ?? '-';
    }
  }
}
